import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Loader } from '@hivebrite/design-system/Loader';

import type { SliderItemUIProps } from 'hb-builder/components/BuilderComponents/block/Gallery/components/SliderItem/components/SliderItemUI';
import type {
  StyledImageProps,
  StyledTextProps,
} from 'hb-builder/components/BuilderComponents/block/Gallery/types';
import LoadingBox from 'hb-builder/components/common/LoadingComponents/LoadingBox';
import { BACKGROUND_POSITION } from 'hb-builder/constants/imageArrangement';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { SliderItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/galleryItemAttributes';

export type SliderCardProps = Omit<SliderItemUIProps, 'toggleDrawer'> & {
  item: DocumentItem<SliderItemAttributes>;
  onClickSliderItem: () => void;
};

const SliderCard = ({
  item,
  fontFamily,
  subdomainNetworkUrl,
  textColor,
  onClickSliderItem,
}: SliderCardProps) => {
  const {
    _imageData,
    backgroundPosition,
    caption,
    galleryImage,
    imageAlt,
    url,
    isLoadingSrc,
  } = item.attributes || {};

  const { id } = item;

  const image = _imageData || galleryImage || url;

  const fullImageUrl =
    image?.startsWith('/images/missing') && subdomainNetworkUrl
      ? `${subdomainNetworkUrl}${image}`
      : image;

  return (
    <Wrapper key={id}>
      {image && (
        <>
          <ImageWrapper
            dataTestId="slider-card-image-wrapper"
            onClick={onClickSliderItem}
          >
            <StyledImage
              dataTestId="slider-card-image"
              $hasCaption={!!caption}
              $bgPos={backgroundPosition || BACKGROUND_POSITION.CENTER}
              $image={fullImageUrl}
            >
              {isLoadingSrc && (
                <LoadingBox>
                  <Loader size={40} />
                </LoadingBox>
              )}
              {imageAlt && (
                <span
                  data-testid="slider-card-alt"
                  role="img"
                  aria-label={imageAlt}
                />
              )}
            </StyledImage>
          </ImageWrapper>
          <StyledText color={textColor} $fontFamily={fontFamily}>
            {caption}
          </StyledText>
        </>
      )}
    </Wrapper>
  );
};

export default SliderCard;

const Wrapper = styled(Box).attrs(() => ({
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
}))<StyledTextProps>`
  font-family: ${({ $fontFamily }) => $fontFamily};
  ${({ theme }) => css`
    border-radius: ${theme.themeVariables.globalBorderRadius}px;
  `}
`;

const StyledImage = styled(Box).attrs(() => ({
  height: '100%',
  width: '100%',
  overflow: 'hidden',
  px: { s: 2, xl: 0 },
}))<StyledImageProps>`
  background-image: ${({ $image }) => ($image ? `url(${$image})` : null)};
  background-position: ${({ $bgPos }) => $bgPos};
  background-repeat: no-repeat;
  background-size: cover;
  position: relative;

  &:after {
    border-radius: 10px;
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.3);
    background: linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.6) 0%,
      rgba(0, 0, 0, 0) 30%
    );
    opacity: ${({ $hasCaption }) => ($hasCaption ? 1 : 0)};
  }

  ${({ theme }) => css`
    border-radius: ${theme.themeVariables.globalBorderRadius}px;
  `}
`;

const ImageWrapper = styled(Box).attrs(() => ({
  height: { xl: 206, l: 192, m: 143, s: 217 },
  width: { xl: 326, l: 304, m: 226, s: '100%' },
  mt: 1,
  overflow: 'hidden',
  position: 'relative',
}))``;

const StyledText = styled(Box).attrs(() => ({
  position: 'absolute',
  bottom: 2,
  left: 2,
  mr: { xl: 9, _: 0 },
}))<StyledTextProps>`
  font-family: ${({ $fontFamily }) => $fontFamily};
  word-break: break-word;
`;
